import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner } from '../components/Spinner';
import { useAuth } from './useAuth';

/** A centred full-viewport spinner shown while the session is being restored. */
function SessionLoading(): JSX.Element {
  return (
    <div className="flex min-h-full items-center justify-center p-8">
      <Spinner size={28} label="Restoring your session…" />
    </div>
  );
}

/**
 * Route guard for authenticated areas.
 *
 * While the session is restoring it shows a spinner; once resolved it either
 * renders the protected subtree or redirects to `/login` (replacing history so
 * Back does not loop through the guard).
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { status } = useAuth();
  if (status === 'loading') {
    return <SessionLoading />;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/**
 * Inverse guard for public-only routes (e.g. `/login`): an already-authenticated
 * user is redirected to the app root rather than seeing the login form again.
 */
export function PublicOnly({ children }: { children: ReactNode }): JSX.Element {
  const { status } = useAuth();
  if (status === 'loading') {
    return <SessionLoading />;
  }
  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
